import { FC } from 'react';

import Close from '@mui/icons-material/Close';
import CopyAllOutlined from '@mui/icons-material/CopyAllOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import json from 'react-syntax-highlighter/dist/cjs/languages/hljs/json';
import Syntax from 'react-syntax-highlighter/dist/cjs/light';
import atomOneDarkReasonable from 'react-syntax-highlighter/dist/cjs/styles/hljs/atom-one-dark-reasonable';

Syntax.registerLanguage('json', json);

type SyntaxHighlighterDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  code: string;
  dialogSx?: SxProps<Theme>;
  syntaxLanguage?: string;
  syntaxStyles: React.CSSProperties;
  onCopy: () => void;
  onDownloadRequested?: () => void;
  copyTooltip?: string;
  downloadTooltip?: string;
};

export const SyntaxHighlighterDialog: FC<SyntaxHighlighterDialogProps> = ({
  isOpen,
  onClose,
  title,
  syntaxLanguage,
  code,
  dialogSx,
  syntaxStyles,
  onCopy,
  onDownloadRequested,
  copyTooltip,
  downloadTooltip,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      sx={dialogSx}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" fontWeight="500" flexGrow={1}>
          {title}
        </Typography>

        <IconButton onClick={onClose} size="small">
          <Close color="action" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack gap={2}>
          <Syntax
            language={syntaxLanguage ?? 'json'}
            style={atomOneDarkReasonable}
            showLineNumbers
            wrapLongLines
            customStyle={syntaxStyles}
          >
            {code}
          </Syntax>
          <Grid container gap={1}>
            {onDownloadRequested && (
              <LoadingButton
                variant="outlined"
                size="small"
                sx={{
                  borderColor: theme.palette.text.secondary,
                  color: theme.palette.text.primary,
                  '& .MuiSvgIcon-root': {
                    color: theme.palette.text.secondary,
                  },
                }}
                endIcon={<DownloadOutlined />}
                onClick={onDownloadRequested}
                title={downloadTooltip ?? ''}
              >
                {downloadTooltip ?? ''}
              </LoadingButton>
            )}
            <LoadingButton
              variant="outlined"
              size="small"
              sx={{
                borderColor: theme.palette.text.secondary,
                color: theme.palette.text.primary,
                '& .MuiSvgIcon-root': {
                  color: theme.palette.text.secondary,
                },
              }}
              endIcon={<CopyAllOutlined />}
              onClick={onCopy}
              title={copyTooltip ?? ''}
            >
              {copyTooltip ?? ''}
            </LoadingButton>
          </Grid>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
